import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  fetchYoutubeVideo,
  fetchYoutubeVideos,
  updateYoutubeVideo,
  createYoutubeVideo,
  destroyYoutubeVideo,
  reorderYoutubeVideos
} from "../../actions/mine_craft/youtubeVideosActions";

export interface YoutubeVideoInterface {
  id: number;
  app_id: number;
  url: string;
  title: string;
  is_new: boolean;
  thumbnail_url: string;
  yt_thumbnail_url: string;
  preview_url: string;
  order: number;
  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: YoutubeVideoInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,
};

const adaptor = createEntityAdapter<YoutubeVideoInterface>();

const slice = createSlice({
  name: 'youtubeVideos',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchYoutubeVideos.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchYoutubeVideos.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.youtube_videos;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchYoutubeVideos.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchYoutubeVideo.fulfilled, (state, action) => {
        const youtubeVideo = Object.values(action.payload.youtube_video)[0];
        state.entities[youtubeVideo.id] = youtubeVideo;
      })

      // #create
      .addCase(createYoutubeVideo.fulfilled, (state, action) => {
        const youtubeVideo = Object.values(action.payload.youtube_video)[0];
        state.entities[youtubeVideo.id] = youtubeVideo;
        state.ids.unshift(youtubeVideo.id);
      })
      .addCase(createYoutubeVideo.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateYoutubeVideo.fulfilled, (state, action) => {
        const youtubeVideo = Object.values(action.payload.youtube_video)[0];
        state.entities[youtubeVideo.id] = youtubeVideo;
      })
      .addCase(updateYoutubeVideo.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #destroy
      .addCase(destroyYoutubeVideo.fulfilled, (state, action) => {
        adaptor.removeOne(state, action.payload.id);
      })

      // #reorder
      .addCase(reorderYoutubeVideos.fulfilled, (state, action) => {
        state.ids = action.payload.ids;
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError
} = slice.actions;

export const {
  selectById: selectYoutubeVideoById,
  selectIds: selectYoutubeVideoIds,
  selectEntities: selectYoutubeVideoEntities,
  selectAll: selectAllYoutubeVideos,
  selectTotal: selectTotalYoutubeVideos
} = adaptor.getSelectors<RootState>(state => state.mineCraft.youtubeVideos);
